import type { FC } from 'react';
import { ImageWithScanEffectComponent } from '@/app/lib/components/ImageWithScanEffect';
import { useResolvedImageSrc } from '@/app/lib/hooks';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { optStr, str } from '@/app/lib/a2ui/props.ts';
import { useIntl } from 'react-intl';
import { messages } from '@/app/lib/localization/messages.ts';
import { ExploreContent } from '../ExploreContent.tsx';

export type ImageProps = {
  src: string;
  alt?: string;
};

const ImageComponent: FC<{ argumentsProps: ImageProps }> = ({ argumentsProps }) => {
  const { src, alt } = argumentsProps;
  const intl = useIntl();
  const { resolvedSrc, error, handleImgError } = useResolvedImageSrc(src);

  if (!src) {
    return null;
  }
  if (error) {
    return (
      <div className="text-destructive text-sm">
        {intl.formatMessage(messages.imageError, { reason: error })}
      </div>
    );
  }
  if (resolvedSrc === null) {
    return (
      <div className="text-muted-foreground text-sm">
        {intl.formatMessage(messages.imageLoading)}
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 bg-transparent text-center">
      <div className="relative w-full">
        <ImageWithScanEffectComponent src={resolvedSrc} alt={alt} onError={handleImgError} />
      </div>
    </div>
  );
};

export default function Image(props: { argumentsProps: ImageProps }) {
  return <ImageComponent {...props} />;
}

export const ImageSurface: FC<A2uiNodeViewProps> = ({ node }) => {
  const src = str(node.props.src);
  const alt = optStr(node.props.alt);
  const image = <Image argumentsProps={{ src, alt }} />;
  return (
    <div className="w-full min-w-0">
      {src && alt?.trim() ? (
        <ExploreContent node={node} selection={{ src, alt }} subject={alt}>
          {image}
        </ExploreContent>
      ) : (
        image
      )}
    </div>
  );
};
